/**
 * A LENS AS A SENTENCE, drawn the way the lens bar draws it but not editable:
 * a chip per term, blue for "is" and amber for "is not", with "and", "not" and
 * "or" in grey between. Follow this lead named the lens as one plain string,
 * which read as a wall of words after "Follow".
 */
import { tagTerms } from '../filters';
import { scopeLabel } from './reportsView';

const dimLabel = (d: string) => d.toLowerCase().replace(/_/g, ' ');

export default function LensPhrase({
  tags,
  verified = null,
  by = null,
}: {
  tags: string[];
  verified?: string | null;
  by?: string | null;
}) {
  const terms = tagTerms(tags);
  // Confidence and "by you", in the reports' own words.
  const rest = scopeLabel({ tags: [], verified: verified as never, by });
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="lens-phrase">
      <span>{terms.length ? 'items tagged' : 'items'}</span>{' '}
      {terms.map((t, i) => {
        const joiner = [i > 0 ? 'and' : '', t.negated ? 'not' : ''].filter(Boolean).join(' ');
        return (
          <span key={t.key} className="inline-flex items-center gap-1">
            {joiner && <span className="text-gray-500 dark:text-gray-400">{joiner}</span>}{' '}
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ${
                t.negated
                  ? 'bg-amber-100 text-amber-900 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-700'
                  : 'bg-sky-100 text-sky-900 ring-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:ring-sky-700'
              }`}
            >
              <span className="opacity-75">{dimLabel(t.dimension)}:</span>{' '}
              {t.values.map((v, j) => (
                <span key={v}>
                  {j > 0 && <span className="opacity-60"> or </span>}
                  {v}
                </span>
              ))}
            </span>
          </span>
        );
      }).flatMap((el, i) => (i ? [' ', el] : [el]))}
      {rest && (
        <span className="inline-flex items-center gap-1">
          {terms.length > 0 && <span className="text-gray-500 dark:text-gray-400">and</span>}{' '}
          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-900 ring-1 ring-sky-300 dark:bg-sky-900/50 dark:text-sky-100 dark:ring-sky-700">
            {rest}
          </span>
        </span>
      )}
    </span>
  );
}
